"use client";

import React, { useState, useEffect } from 'react';
import Script from 'next/script';
import { Copy, CheckCheck } from 'lucide-react';
import { useTranslations } from 'next-intl';

// 扩展 Window 接口以包含 a2a 和 a2a_config
declare global {
    interface Window {
        a2a?: {
            init: () => void;
            [key: string]: unknown;
        };
        a2a_config?: {
            locale?: string;
            onclick?: number;
            num_services?: number;
            prioritize?: string[];
            title?: string;
            [key: string]: unknown;
        };
    }
}

interface ShareButtonsProps {
    url: string;
    title: string;
    readingId?: string;
    asStaticPage?: boolean;
    shareId?: string;
    gradientClass?: string;
}

export default function ShareButtons({
    url,
    title,
    gradientClass = "from-brand-purple to-brand-pink",
}: ShareButtonsProps) {
    const t = useTranslations('share');
    const [isMounted, setIsMounted] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const maxRetries = 3;

    // 组件挂载后设置状态
    useEffect(() => {
        setIsMounted(true);

        // AddToAny 配置
        if (typeof window !== 'undefined') {
            window.a2a_config = window.a2a_config || {};
            window.a2a_config.locale = "zh-CN";
            window.a2a_config.onclick = 1;
            window.a2a_config.num_services = 12;
            window.a2a_config.prioritize = ["wechat", "copy_link", "weibo", "facebook"];
            window.a2a_config.title = title;
        }

        // 检查并初始化 AddToAny
        const initAddToAny = () => {
            if (typeof window !== 'undefined' && window.a2a && typeof window.a2a.init === 'function') {
                try {
                    window.a2a.init();
                    console.log('AddToAny初始化成功');
                } catch (error) {
                    console.error('AddToAny初始化失败:', error);
                    // 如果初始化失败且尚未达到最大重试次数，则重试
                    if (retryCount < maxRetries) {
                        const nextRetryCount = retryCount + 1;
                        setRetryCount(nextRetryCount);
                        setTimeout(() => {
                            console.log(`重试初始化AddToAny (${nextRetryCount}/${maxRetries})...`);
                            initAddToAny();
                        }, 1000);
                    } else {
                        console.error('AddToAny初始化失败，已达到最大重试次数');
                    }
                }
            }
        };

        // 如果 AddToAny 已加载，尝试初始化
        initAddToAny();

        // 添加脚本加载失败的处理
        const handleScriptError = () => {
            console.error('AddToAny脚本加载失败');
            document.querySelector('.backup-share-button')?.classList.remove('hidden');
        };

        // 清理函数
        return () => {
            // 删除任何可能存在的事件监听器，防止内存泄漏
            const script = document.querySelector('script[src="https://static.addtoany.com/menu/page.js"]');
            if (script) {
                script.removeEventListener('error', handleScriptError);
            }
        };
    }, [title, retryCount]);

    // 复制链接到剪贴板（备用）
    const handleCopyLink = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isMounted) return;

        try {
            navigator.clipboard.writeText(url);
            setIsCopied(true);
            console.log("链接已成功复制到剪贴板");

            // 3秒后重置复制状态
            setTimeout(() => {
                setIsCopied(false);
            }, 3000);
        } catch (err) {
            console.error('Failed to copy:', err);
            window.alert("无法复制链接，请重试");
        }
    };

    // 渲染骨架屏
    const renderSkeleton = () => (
        <div className="flex flex-wrap gap-3 justify-center opacity-60">
            <div className="w-10 h-10 rounded-full bg-gray-700/30 animate-pulse"></div>
            <div className="w-10 h-10 rounded-full bg-gray-700/30 animate-pulse"></div>
            <div className="w-10 h-10 rounded-full bg-gray-700/30 animate-pulse"></div>
            <div className="w-10 h-10 rounded-full bg-gray-700/30 animate-pulse"></div>
            <div className="w-10 h-10 rounded-full bg-gray-700/30 animate-pulse"></div>
            <div className="w-10 h-10 rounded-full bg-gray-700/30 animate-pulse"></div>
        </div>
    );

    // 渲染按钮内容
    const renderContent = () => (
        <div className="text-center">
            {/* AddToAny 分享按钮 */}
            <div className="a2a_kit a2a_kit_size_32 a2a_default_style flex flex-wrap gap-3 justify-center"
                data-a2a-url={url}
                data-a2a-title={title}
                style={{ position: 'relative', zIndex: 100, pointerEvents: 'auto' }}>
                <a className="a2a_button_facebook"></a>
                <a className="a2a_button_email"></a>
                <a className="a2a_button_wechat"></a>
                <a className="a2a_button_telegram"></a>
                <a className="a2a_button_google_gmail"></a>
                <a className="a2a_button_reddit"></a>
                <a className="a2a_button_hacker_news"></a>
                <a className="a2a_button_snapchat"></a>
                <a className="a2a_button_x"></a>
                <a className="a2a_button_copy_link"></a>
                <a className="a2a_button_pinterest"></a>
                <a className="a2a_dd" href="https://www.addtoany.com/share"></a>
            </div>

            {/* 备用复制链接按钮（如果AddToAny按钮加载失败可以使用这个） */}
            <div className="mt-3 backup-share-button hidden">
                <button
                    type="button"
                    onClick={handleCopyLink}
                    className={`px-4 py-2 bg-gradient-to-r ${gradientClass} hover:brightness-110 transition-all rounded-full flex items-center justify-center gap-2 shadow-lg cursor-pointer mx-auto`}
                    style={{ pointerEvents: 'auto' }}
                    aria-label={t('copy_link')}
                >
                    {isCopied ? (
                        <>
                            <CheckCheck className="h-5 w-5 text-white" />
                            <span className="text-white">{t('copied')}</span>
                        </>
                    ) : (
                        <>
                            <Copy className="h-5 w-5 text-white" />
                            <span className="text-white">{t('copy_link')}</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    // 主渲染函数
    return (
        <div
            className="relative"
            style={{
                pointerEvents: 'auto',
                position: 'relative',
                zIndex: 999
            }}
        >
            {!isMounted ? renderSkeleton() : renderContent()}

            {/* AddToAny 脚本 */}
            <Script
                src="https://static.addtoany.com/menu/page.js"
                strategy="afterInteractive"
                onLoad={() => {
                    console.log('AddToAny脚本已加载');
                    if (typeof window !== 'undefined' && window.a2a && typeof window.a2a.init === 'function') {
                        try {
                            window.a2a.init();
                            console.log('AddToAny初始化成功');
                        } catch (error) {
                            console.error('AddToAny脚本加载后初始化失败:', error);
                            document.querySelector('.backup-share-button')?.classList.remove('hidden');
                        }
                    }
                }}
                onError={() => {
                    console.error('AddToAny脚本加载失败');
                    document.querySelector('.backup-share-button')?.classList.remove('hidden');
                }}
            />
        </div>
    );
} 